<template>
  <div>
    <h2 class="mt-9 font-20 font-weight-bold black2-color">Policy details</h2>
    <BaseLabel :text="LABEL_CONSTANTS.BASIS_OF_ADVICE" class="mt-3 mb-2" />
    <BaseSelect
      dense
      v-model="questionBasisOfAdvice.Value"
      :items="basisOfAdviceItems"
      :data-uiid="questionBasisOfAdvice.InterfaceKey"
      :hide-details="false"
      :selectedDefault="LABEL_CONSTANTS.SELECT_DEFAULT_PLACEHOLDER"
      :rules="{
        required: {
          required: true,
          fieldLabel: LABEL_CONSTANTS.BASIS_OF_ADVICE,
        },
      }"
      :style="{
        'max-width': $vuetify.breakpoint.lgAndUp ? '280px' : '100%',
      }"
      :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
    />

    <BaseLabel text="Policy start date" class="mt-3 mb-2" />
    <BaseInputDate
      v-model="questionPolicyStartDate.Value"
      dataUiid="PolicyStartDate"
      :vid="questionPolicyStartDate.InterfaceKey"
      :rules="policyStartDateRules"
      :hide-details="false"
      :style="{
        'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
      }"
      :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
    />

    <BaseLabel
      text="How would you like to receive your documents?"
      class="mt-2 mb-2"
    />
    <BaseRadio
      pslStyle="button"
      v-model="questionDocumentDeliveryPreference.Value"
      vid="DocumentDeliveryPreference"
      dataUiid="DocumentDeliveryPreference"
      :hideDetails="false"
      :items="documentDeliveryPreferenceOptions"
      :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
    />

    <BaseLabel
      :text="LABEL_CONSTANTS.SPECIFY_CORRESPONDENCE_ADDRESS"
      class="mt-3 mb-2"
    />
    <BaseToggleButton
      v-model="questionUseCorrespondenceAddress.Value"
      :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
      :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
      :styleClass="!questionUseCorrespondenceAddress.Value ? 'mb-10' : ''"
      :hide-details="false"
      :toggleButtonText="{
        yes: 'Yes',
        no: 'No',
        yesDataUiid: `${questionUseCorrespondenceAddress.InterfaceKey}_Yes`,
        noDataUiid: `${questionUseCorrespondenceAddress.InterfaceKey}_No`,
      }"
    />

    <div v-if="questionUseCorrespondenceAddress.Value" class="mt-10">
      <p class="sub-help-text" v-if="!isShowCorrespondenceAddressLabel">
        {{ LABEL_CONSTANTS.SPECIFY_CORRESPONDENCE_ADDRESS_HINT_TEXT }}
      </p>
      <BaseAddressLookup
        dataUiid="CorrespondenceAddress_Find_Property"
        showManualAddressForm
        manualAddressFormText="Correspondence address"
        :placeholder="
          $vuetify.breakpoint.lgAndUp
            ? 'Start typing your address or postcode...'
            : 'Start typing...'
        "
        manualFormUiid="CorrespondenceAddress"
        id="address-lookup-correspondence"
        :address="correspondenceAddress"
        @address-selected="
          (response) => addressSelected(response, correspondenceAddress)
        "
        @is-address-selected="correspondenceAddressLabel"
        @use-address-finder="isEnteringAddressManually = false"
        @enter-address-manually="isEnteringAddressManually = true"
        :hideDetails="false"
        :rules="{
          required: {
            required: true,
            fieldLabel: 'Find your property',
          },
        }"
        :style="{
          'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
        }"
        :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
      />
    </div>
  </div>
</template>

<script>
import { addFieldLabel, convertToBool } from "@/common/utils";
import { mapActions, mapGetters, mapState } from "vuex";
import {
  BASIS_OF_ADVICE,
  DOCUMENT_DELIVERY_PREFERENCE_OPTIONS,
  setItemDataUiids,
} from "@/constants/component.constants.js";
import * as LABEL_CONSTANTS from "@/constants/label.constants";
import * as ERROR_MESSAGES from "@/constants/message.constants.js";
import { DATE_REGEX } from "@/constants/regex-patterns.constants";
import {
  APPLICATION_MODULE,
  QUESTIONS_MODULE,
} from "@/store/types/module-types";

import BaseAddressLookup from "@/components/BaseComponents/BaseAddressLookup";
import BaseInputDate from "@/components/BaseComponents/BaseInputDate";
import BaseLabel from "@/components/BaseComponents/BaseLabel.vue";
import BaseSelect from "@/components/BaseComponents/BaseSelect";
import BaseToggleButton from "@/components/BaseComponents/BaseToggleButton";
import BaseRadio from "@/components/BaseComponents/BaseRadio";
import { addressSelected } from "@/common/addresses";

export default {
  components: {
    BaseLabel,
    BaseSelect,
    BaseInputDate,
    BaseToggleButton,
    BaseAddressLookup,
    BaseRadio,
  },
  data() {
    return {
      questionBasisOfAdvice: {},
      questionUseCorrespondenceAddress: {},
      questionPolicyStartDate: {},
      LABEL_CONSTANTS,
      ERROR_MESSAGES,
      BASIS_OF_ADVICE,
      correspondenceAddress: {},
      isShowCorrespondenceAddressLabel: false,
      isEnteringAddressManually: false,
      questionDocumentDeliveryPreference: {},
    };
  },
  watch: {
    "questionUseCorrespondenceAddress.Value"(value) {
      if (!value) {
        this.resetAddressForm();
        this.isShowCorrespondenceAddressLabel = false;
      }
    },
  },
  computed: {
    ...mapState(APPLICATION_MODULE, {
      applicationQuestionMap: "questionMap",
    }),
    ...mapGetters(APPLICATION_MODULE, {
      selectedQuote: "selectedQuote",
    }),
    ...mapState(QUESTIONS_MODULE, {
      questionsQuestionMap: "questionMap",
      userReference: "userReference",
    }),
    basisOfAdviceItems() {
      return setItemDataUiids("BasisOfAdvice", BASIS_OF_ADVICE);
    },
    policyStartDateRules() {
      return addFieldLabel(
        {
          required: {
            required: true,
          },
          date_valid: {
            errorMessageTemplate: ERROR_MESSAGES.ERROR_INVALID,
          },
          regex: {
            errorMessageTemplate: ERROR_MESSAGES.ERROR_INVALID,
            pattern: DATE_REGEX,
          },
          between_now_and: {
            future: this.selectedQuote?.QuoteExpiryDate,
          },
        },
        "Policy start date"
      );
    },
    documentDeliveryPreferenceOptions() {
      return setItemDataUiids(
        this.questionDocumentDeliveryPreference.InterfaceKey,
        DOCUMENT_DELIVERY_PREFERENCE_OPTIONS
      );
    },
  },
  beforeMount() {
    this.onSetupAndLoadSavedData();
    this.initAddressForm();
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, [
      "setUserReference",
      "refreshSessionAndNextStep",
      "toggleGlobalSaveAndExitDialog",
    ]),
    addressSelected,
    onSetupAndLoadSavedData() {
      this.questionBasisOfAdvice = this.applicationQuestionMap["BasisOfAdvice"];
      if (!this.questionBasisOfAdvice.Value) {
        this.questionBasisOfAdvice.Value = "NonAdvised";
      }

      this.questionUseCorrespondenceAddress =
        this.applicationQuestionMap["UseCorrespondenceAddress"];
      if (this.questionUseCorrespondenceAddress) {
        this.questionUseCorrespondenceAddress.Value = convertToBool(
          this.questionUseCorrespondenceAddress.Value
        );
      }
      this.questionPolicyStartDate =
        this.applicationQuestionMap["PolicyStartDate"];
      this.questionDocumentDeliveryPreference =
        this.applicationQuestionMap["DocumentDeliveryPreference"];
    },
    initAddressForm() {
      this.correspondenceAddress = {
        PostCode: this.applicationQuestionMap["CorrespondencePostCode"],
        Street: this.applicationQuestionMap["CorrespondenceStreet"],
        District: this.applicationQuestionMap["CorrespondenceDistrict"],
        Town: this.applicationQuestionMap["CorrespondenceTown"],
        County: this.applicationQuestionMap["CorrespondenceCounty"],
      };
    },
    correspondenceAddressLabel(val) {
      this.isShowCorrespondenceAddressLabel = val;
    },
    resetAddressForm() {
      (this.applicationQuestionMap["CorrespondencePostCode"].Value = ""),
        (this.applicationQuestionMap["CorrespondenceStreet"].Value = ""),
        (this.applicationQuestionMap["CorrespondenceDistrict"].Value = ""),
        (this.applicationQuestionMap["CorrespondenceTown"].Value = ""),
        (this.applicationQuestionMap["CorrespondenceCounty"].Value = "");
    },
  },
};
</script>

<style lang="scss" scoped>
.sub-help-text {
  margin-top: 16px;
  max-width: 630px;
  font-size: 14px;
  color: #272727;
}
</style>
